<script>
import Button from "@/shared/ui/button/Button.vue";
import { Input } from "@/shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/ui/popover";
import { Checkbox } from "@/shared/ui/checkbox";
import { Badge } from "@/shared/ui/badge";
import { DateRangePicker } from "@/shared/ui/date-picker";

export default {
  name: "HereditaryCaseFilter",
  components: {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Checkbox,
    Badge,
    DateRangePicker,
  },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:modelValue", "change", "reset"],
  computed: {
    filters: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    orderByValue: {
      get() {
        const val = this.filters.order_by;
        return val != null && val !== "" ? String(val) : undefined;
      },
      set(value) {
        this.filters.order_by = value;
        this.emitChange();
      },
    },
    selectedStatusCount() {
      return this.filters.status__in?.length || 0;
    },
  },
  methods: {
    emitChange() {
      this.$emit("change");
    },
    resetFilters() {
      this.$emit("reset");
    },
    isStatusSelected(value) {
      return this.filters.status__in?.includes(value) || false;
    },
    toggleStatus(value) {
      const arr = this.filters.status__in || [];
      const idx = arr.indexOf(value);
      if (idx === -1) {
        arr.push(value);
      } else {
        arr.splice(idx, 1);
      }
      this.filters.status__in = [...arr];
      this.emitChange();
    },
  },
};
</script>

<template>
  <div class="filters">
    <div class="overflow-x-auto">
      <div class="filters-row">
        <Input
          v-model="filters.number"
          :placeholder="$t('№ насл. дела')"
          class="filter-select"
          clearable
          @input="emitChange"
        />

        <DateRangePicker
          :start-date="filters.date_from"
          :end-date="filters.date_to"
          :placeholder="$t('Период')"
          class="filter-select w-[330px]"
          @update:start-date="
            (val) => {
              filters.date_from = val;
              emitChange();
            }
          "
          @update:end-date="
            (val) => {
              filters.date_to = val;
              emitChange();
            }
          "
        />
        <Popover>
          <PopoverTrigger as-child>
            <Button variant="outline" class="filter-select justify-between">
              <span class="text-muted-foreground text-xs">{{
                $t("Статус")
              }}</span>
              <Badge v-if="selectedStatusCount > 0" variant="secondary">
                {{ selectedStatusCount }}
              </Badge>
            </Button>
          </PopoverTrigger>
          <PopoverContent class="w-[220px] p-1.5">
            <div class="flex flex-col gap-0.5">
              <label
                v-for="status in statusOptions"
                :key="status.value"
                class="flex items-center gap-2 px-1.5 py-1 rounded cursor-pointer hover:bg-muted"
              >
                <Checkbox
                  size="small"
                  :model-value="isStatusSelected(status.value)"
                  @change="toggleStatus(status.value)"
                />
                <span class="text-sm">{{ status.label }}</span>
              </label>
            </div>
          </PopoverContent>
        </Popover>
        <Select v-model="orderByValue">
          <SelectTrigger
            class="w-full filter-select"
            @clear="orderByValue = undefined"
          >
            <SelectValue :placeholder="$t('Сортировка')" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="-created_at">{{
              $t("По дате (новые)")
            }}</SelectItem>
            <SelectItem value="created_at">{{
              $t("По дате (старые)")
            }}</SelectItem>
            <SelectItem value="number">{{
              $t("По номеру (возр.)")
            }}</SelectItem>
            <SelectItem value="-number">{{
              $t("По номеру (убыв.)")
            }}</SelectItem>
            <SelectItem value="status">{{
              $t("По статусу (возр.)")
            }}</SelectItem>
            <SelectItem value="-status">{{
              $t("По статусу (убыв.)")
            }}</SelectItem>
          </SelectContent>
        </Select>
        <Input
          v-model="filters.deceased_fio"
          :placeholder="$t('ФИО умершего')"
          class="filter-select"
          @input="emitChange"
          clearable
        />
        <Input
          v-model="filters.deceased_iin"
          :placeholder="$t('ИИН умершего')"
          class="filter-select"
          clearable
          @input="emitChange"
        />
        <Input
          v-model="filters.notary_identifier"
          :placeholder="$t('ИИН нотариуса')"
          class="filter-select"
          clearable
          @input="emitChange"
        />
        <Input
          v-model="filters.identifier"
          :placeholder="$t('ИИН участника')"
          class="filter-select"
          clearable
          @input="emitChange"
        />
      </div>
    </div>
    <Button variant="outline" @click="resetFilters">
      {{ $t("Сбросить фильтр") }}
    </Button>
  </div>
</template>

<style scoped lang="scss">
.filters {
  @apply flex justify-between items-center gap-4;
}

.filters-row {
  @apply flex items-center gap-2 w-max;
}

.filter-select {
  min-width: 200px;
}
</style>
